/**
 * @prettier
 * @flow
 */
import { createSlice } from '@reduxjs/toolkit';
import { getAccessTokenInLocalStorage } from 'src/localStorage/getAccessTokenInLocalStorage';
import { getAccountIdInLocalStorage } from 'src/localStorage/getAccountIdInLocalStorage';
import { getEmailInLocalStorage } from 'src/localStorage/getEmailInLocalStorage';
import { getSignedOutReasonInLocalStorage } from 'src/localStorage/getSignedOutReasonInLocalStorage';
import { setAccessTokenInLocalStorage } from 'src/localStorage/setAccessTokenInLocalStorage';
import { setAccountIdInLocalStorage } from 'src/localStorage/setAccountIdInLocalStorage';
import { setEmailInLocalStorage } from 'src/localStorage/setEmailInLocalStorage';
import { setRefreshTokenInLocalStorage } from 'src/localStorage/setRefreshTokenInLocalStorage';
import { setSignedOutReasonInLocalStorage } from 'src/localStorage/setSignedOutReasonInLocalStorage';
import type { AppActions } from 'src/types/AppActions';
import type { AppVm } from 'src/types/AppVm';
import type { AccountId, EmailAddress } from 'src/types/Id';

const signedIn = !!getAccessTokenInLocalStorage();

const initialState: AppVm = {
    signedIn: signedIn,
    signInFailureReason: undefined,
    signedOutReason: signedIn ? undefined : getSignedOutReasonInLocalStorage(),
    email: signedIn ? getEmailInLocalStorage() : undefined,
    accountId: signedIn ? getAccountIdInLocalStorage() : undefined,
    appSnackBar: {
        open: false,
        message: '',
        autoHideDuration: 2000,
    },
};

const reducers: AppActions = {
    signInUser: (app: AppVm, { payload: { email, accountId, accessToken, refreshToken } }: { payload: { email: EmailAddress, accountId: AccountId, accessToken: string, refreshToken: string } }) => {
        setEmailInLocalStorage(email);
        setAccountIdInLocalStorage(accountId);
        setAccessTokenInLocalStorage(accessToken);
        setRefreshTokenInLocalStorage(refreshToken);
        setSignedOutReasonInLocalStorage(undefined);

        app.signedIn = true;
        app.email = email;
        app.accountId = accountId;
        app.signInFailureReason = undefined;
    },
    setSignInFailureReason: (app: AppVm, { payload: signInFailureReason }: { payload: string }) => {
        setSignedOutReasonInLocalStorage(undefined);

        app.signInFailureReason = signInFailureReason;
    },
    signOutUser: (app: AppVm, { payload: { signedOutReason } }: { payload: { signedOutReason: string } }) => {
        setEmailInLocalStorage(undefined);
        setAccountIdInLocalStorage(undefined);
        setAccessTokenInLocalStorage(undefined);
        setRefreshTokenInLocalStorage(undefined);
        setSignedOutReasonInLocalStorage(signedOutReason);

        app.signedIn = false;
        app.signedOutReason = signedOutReason;
        app.email = undefined;
        app.accountId = undefined;
    },
    showAppSnackBarMessage: (app: AppVm, { payload: { message, autoHideDuration } }: { payload: { message: string, autoHideDuration?: number } }) => {
        app.appSnackBar = {
            open: true,
            message: message,
            autoHideDuration: autoHideDuration ?? initialState.appSnackBar.autoHideDuration,
        };
    },
    closeAppSnackBar: (app: AppVm) => {
        app.appSnackBar = {
            open: false,
            message: initialState.appSnackBar.message,
            autoHideDuration: initialState.appSnackBar.autoHideDuration,
        };
    },
};

export const slice: { reducer: any, actions: AppActions } = createSlice({
    name: 'app',
    initialState,
    reducers,
});

export const actions: AppActions = slice.actions;
